/**
 * Rules the sign-up form and the server must agree on.
 *
 * Both import this file. A strength meter that says "strong" for a password the server
 * then rejects is worse than no meter at all.
 */

export const MIN_PASSWORD_LENGTH = 10

/** Deliberately small. A long list of "banned passwords" is security theatre; these are
 *  the ones that actually turn up, and length does the rest of the work. */
const COMMON = [
  'password',
  'qwerty',
  'letmein',
  'welcome',
  'admin',
  'iloveyou',
  'monkey',
  'dragon',
  'football',
  'betfair',
  'kellystake',
  '123456',
  '12345678',
  'abc123',
]

export interface PasswordVerdict {
  /** 0–100, for the meter. */
  score: number
  label: 'Too weak' | 'Weak' | 'Medium' | 'Strong'
  /** Whether the server will accept it. */
  acceptable: boolean
  /** What to do about it, in one sentence. Shown under the meter. */
  hint: string
}

export function assessPassword(password: string, email = ''): PasswordVerdict {
  const pw = password ?? ''
  const lower = pw.toLowerCase()

  if (pw.length === 0) {
    return { score: 0, label: 'Too weak', acceptable: false, hint: `At least ${MIN_PASSWORD_LENGTH} characters.` }
  }

  const local = email.split('@')[0]?.toLowerCase() ?? ''
  if (local.length >= 3 && lower.includes(local)) {
    return {
      score: 10,
      label: 'Too weak',
      acceptable: false,
      hint: 'Your password contains your email address. Pick something unrelated to it.',
    }
  }

  const hit = COMMON.find((c) => lower.includes(c))
  if (hit) {
    return {
      score: 15,
      label: 'Too weak',
      acceptable: false,
      hint: `Contains "${hit}", which is one of the first things an attacker tries. Use an unrelated phrase.`,
    }
  }

  // A single repeated or sequential run is a long password that is not a strong one.
  if (/^(.)\1+$/.test(pw) || /^(0123456789|abcdefghij)/.test(lower)) {
    return { score: 10, label: 'Too weak', acceptable: false, hint: 'Avoid repeated or sequential characters.' }
  }

  if (pw.length < MIN_PASSWORD_LENGTH) {
    return {
      score: Math.round((pw.length / MIN_PASSWORD_LENGTH) * 35),
      label: 'Too weak',
      acceptable: false,
      hint: `At least ${MIN_PASSWORD_LENGTH} characters — you have ${pw.length}.`,
    }
  }

  // Length carries most of the entropy; variety is a bonus, not a checkbox.
  const classes =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/[0-9]/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0)

  const lengthScore = Math.min(60, (pw.length - MIN_PASSWORD_LENGTH + 4) * 6)
  const varietyScore = classes * 10
  const score = Math.min(100, lengthScore + varietyScore)

  if (score >= 75) {
    return {
      score,
      label: 'Strong',
      acceptable: true,
      hint: 'long enough and varied enough that there is nothing worth changing.',
    }
  }
  if (score >= 50) {
    return {
      score,
      label: 'Medium',
      acceptable: true,
      hint: 'Medium strength — a longer phrase or a symbol would make it markedly harder to guess.',
    }
  }
  return {
    score,
    label: 'Weak',
    acceptable: true,
    hint: 'Weak but accepted. A longer passphrase is the single biggest improvement you can make.',
  }
}

export function isValidEmail(email: string): boolean {
  // Deliberately permissive: the only real test of an address is sending to it.
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())
}

// ---------------------------------------------------------------------------
// The session as the client sees it
// ---------------------------------------------------------------------------

export type AccountStatus = 'active' | 'suspended' | 'closed'

export interface SessionUser {
  id: number
  email: string
  displayName: string
  initials: string
  role: 'owner' | 'admin' | 'viewer'
  status: AccountStatus
  emailVerified: boolean
  /** False on a Google account that has never set one — the security screen offers to add it. */
  hasPassword: boolean
  googleLinked: boolean
  createdAt: string
  lastLoginAt: string | null
}

/** Which sign-in methods this deployment has been configured for. */
export interface AuthProviders {
  google: boolean
  /**
   * Whether anyone can sign in or sign up. While false, the auth screens send people to
   * `/waitlist` and the server refuses every route that creates a session.
   */
  accountsOpen: boolean
}

/** The Google identity waiting on consent, as the sign-up screen needs to show it. */
export interface PendingGoogleSignup {
  email: string
  displayName: string | null
}

/**
 * Why a Google sign-in ended on the login screen instead of in the app.
 *
 * Deliberately coarse. The callback is a redirect, so the reason travels in a query
 * string the user can read and edit — it names a state to render, never a detail about
 * an account.
 */
export type GoogleErrorCode =
  | 'cancelled'
  | 'expired'
  | 'unverified_email'
  | 'suspended'
  | 'unavailable'
  | 'failed'

export const GOOGLE_ERRORS: Record<GoogleErrorCode, string> = {
  cancelled: 'Google sign-in was cancelled. Nothing has changed.',
  expired: 'That Google sign-in took too long and expired. Please try again.',
  unverified_email:
    'Google has not verified the email address on that account. Verify it with Google, or sign up with an email and password instead.',
  suspended:
    'This account is suspended. Live trading and sign-in are disabled pending review. Any open positions were flattened automatically.',
  unavailable: 'Google sign-in is not available right now. You can still sign in with your password.',
  failed: 'We could not complete the Google sign-in. Please try again.',
}

export function googleErrorMessage(code: string | null): string | null {
  if (!code) return null
  return GOOGLE_ERRORS[code as GoogleErrorCode] ?? GOOGLE_ERRORS.failed
}

export interface AuthError {
  error: string
  /** Which state the designed screen should show. */
  kind?:
    | 'invalid_credentials'
    | 'locked'
    | 'suspended'
    | 'email_taken'
    | 'weak_password'
    | 'invalid_token'
    | 'accounts_closed'
  /** For a lockout: seconds until sign-in is possible again. */
  retryAfterS?: number
  /** For invalid credentials: how many tries are left before a lockout. */
  attemptsRemaining?: number
}
